import { FC } from 'react';
import { Modal, SwitchInput } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Stack } from '@mui/material';
import { usePredefinedToasts } from 'utils/Toast';
import { useBulkUpdateBenefits } from 'store/benefits';

interface Values {
  isArchived: boolean;
}

const INITIAL_VALUES: Values = {
  isArchived: false,
};

interface Props extends ModalProps {
  closeModal: (param?: { action: 'CLOSE' } | { action: 'SAVE' }) => void;
  benefitIds: string[];
}

export const BenefitsBulkEditModal: FC<Props> = ({
  closeModal,
  benefitIds,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { mutate: bulkUpdate, isLoading: isUpdating } = useBulkUpdateBenefits();

  const onSubmit: SubmitHandler<Values> = (values) => {
    bulkUpdate(
      {
        benefitIds,
        data: {
          isArchived: values.isArchived,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          closeModal({ action: 'SAVE' });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('common.edit')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: isUpdating,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="isArchived"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <SwitchInput
                  label={t('benefits.isArchived.label')}
                  info={t('benefits.isArchived.info')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
